import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"
import DOMPurify from "dompurify"

export default class extends Controller {
  static targets = [
    "personaList",
    "personaButton",
    "selectedPersonaName",
    "questionBlocks",
    "blockContent",
    "seedIntents",
    "removePersonaModal",
    "removePersonaName"
  ]
  static values = {
    saveUrl: String,
    removePersonaUrl: String,
    selectedPersona: { type: String, default: "general" }
  }

  connect() {
    this.selectedPersonaValue = "general"
  }

  selectPersona(event) {
    const button = event.currentTarget
    const persona = button.dataset.persona

    // Update selection state
    this.selectedPersonaValue = persona

    // Update button styles
    this.personaButtonTargets.forEach(btn => {
      if (btn.dataset.persona === persona) {
        btn.classList.remove("text-[#6B7280]", "hover:bg-[#F9FAFB]")
        btn.classList.add("bg-[#F2F3F5]", "text-[#030712]")
      } else {
        btn.classList.remove("bg-[#F2F3F5]", "text-[#030712]")
        btn.classList.add("text-[#6B7280]", "hover:bg-[#F9FAFB]")
      }
    })

    // Update header
    if (this.hasSelectedPersonaNameTarget) {
      this.selectedPersonaNameTarget.textContent = button.textContent.trim()
    }

    // Filter question blocks
    this.filterBlocksByPersona(persona)
  }

  filterBlocksByPersona(persona) {
    const blocks = this.element.querySelectorAll("[data-question-block]")
    blocks.forEach(block => {
      const blockPersona = block.dataset.persona || "general"
      if (persona === "general" || blockPersona === persona || blockPersona === "general") {
        block.classList.remove("hidden")
      } else {
        block.classList.add("hidden")
      }
    })
  }

  addPersona() {
    const name = prompt("Enter persona name:")
    if (!name || !name.trim()) return
    this.addPersonaToList(name.trim())
  }

  addPersonaToList(name) {
    if (!name || !name.trim()) return

    const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")

    // Check if persona already exists
    const existingButton = Array.from(this.personaButtonTargets).find(
      btn => btn.dataset.persona === slug
    )
    if (existingButton) {
      console.warn(`Persona "${name}" already exists`)
      return
    }

    // Create new button
    const button = document.createElement("button")
    button.type = "button"
    button.className = "box-border flex items-center cursor-pointer transition-all duration-200 justify-start px-3 py-2 gap-2 h-9 rounded-md data-[active=true]:bg-[#fafafb] data-[active=false]:bg-transparent text-[#4b5563] data-[active=true]:text-[#1F2937] font-semibold text-sm leading-5 w-full"
    button.dataset.action = "click->tabs#switch"
    button.dataset.tabsTarget = "trigger"
    button.dataset.value = slug
    button.dataset.persona = slug
    button.dataset.guidelineEditorTarget = "personaButton"
    button.textContent = name.trim()

    // Add to persona list
    if (this.hasPersonaListTarget) {
      this.personaListTarget.appendChild(button)
    }

    // TODO: Save to backend
    // this.savePersonas()
  }

  confirmRemovePersona(event) {
    event.stopPropagation()
    this._pendingRemovePersonaName = event.currentTarget.dataset.personaName
    this._pendingRemovePersonaSlug = event.currentTarget.dataset.personaSlug

    // Update modal text with persona name
    if (this.hasRemovePersonaNameTarget) {
      this.removePersonaNameTarget.textContent = this._pendingRemovePersonaName
    }

    // Open the confirmation modal
    if (this.hasRemovePersonaModalTarget) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.removePersonaModalTarget, "modal"
      )
      if (modalController) modalController.open()
    }
  }

  async removePersona() {
    const personaName = this._pendingRemovePersonaName
    const personaSlug = this._pendingRemovePersonaSlug
    if (!personaName) return

    // Close the modal
    if (this.hasRemovePersonaModalTarget) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.removePersonaModalTarget, "modal"
      )
      if (modalController) modalController.close()
    }

    try {
      const response = await fetch(this.removePersonaUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ persona_name: personaName })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to remove persona")
      }

      // Remove sidebar button wrapper
      const wrapper = this.element.querySelector(`[data-persona-slug="${personaSlug}"]`)?.closest(".group")
      if (wrapper) wrapper.remove()

      // Remove content tab panel
      const tabPanel = this.element.querySelector(`[data-tabs-target="content"][data-value="${personaSlug}"]`)
      if (tabPanel) tabPanel.remove()

      // Remove from regenerate form persona tags
      const guidelineGenerator = this.element.closest('[data-controller~="guideline-generator"]') || this.element
      const tag = guidelineGenerator.querySelector(`[data-tag-value="${personaName}"]`)
      if (tag) {
        const tagWrapper = tag.closest('[data-tag-input-guideline-target="tag"]') || tag
        tagWrapper.remove()
      }

      // Switch to General tab if removed persona was selected
      if (this.selectedPersonaValue === personaSlug) {
        const generalButton = this.element.querySelector('[data-tabs-target="trigger"][data-value="general"]')
        if (generalButton) generalButton.click()
      }

      if (window.toastController) {
        window.toastController.success("Persona removed successfully")
      }
    } catch (error) {
      console.error("Remove persona error:", error)
      if (window.toastController) {
        window.toastController.error(error.message)
      }
    } finally {
      this._pendingRemovePersonaName = null
      this._pendingRemovePersonaSlug = null
    }
  }

  copyBlock(event) {
    const block = event.currentTarget.closest("[data-question-block]")
    if (!block) return

    const content = block.querySelector("p, ol")?.textContent || ""

    navigator.clipboard.writeText(content.trim())
      .then(() => {
        // Show feedback
        const button = event.currentTarget
        const originalHTML = button.innerHTML
        button.innerHTML = '<svg class="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17L4 12"/></svg>'
        setTimeout(() => {
          button.innerHTML = originalHTML
        }, 1500)
      })
      .catch(err => {
        console.error("Failed to copy:", err)
      })
  }

  editBlock(event) {
    const block = event.currentTarget.closest("[data-question-block]")
    if (!block) return

    const contentEl = block.querySelector("p, ol")
    if (!contentEl) return

    const currentContent = contentEl.textContent.trim()

    // Create textarea for editing
    const textarea = document.createElement("textarea")
    textarea.value = currentContent
    textarea.className = "w-full px-3 py-2 text-sm border border-[#4427BF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4427BF] resize-none"
    textarea.rows = 4
    textarea.dataset.originalContent = currentContent

    // Replace content with textarea
    const originalElement = contentEl.cloneNode(true)
    contentEl.replaceWith(textarea)

    // Focus textarea
    textarea.focus()
    textarea.select()

    // Handle save on blur or Escape
    const saveEdit = () => {
      const newContent = textarea.value.trim()
      if (newContent !== currentContent) {
        const newP = document.createElement("p")
        newP.className = "text-sm text-[#374151]"
        newP.textContent = newContent
        textarea.replaceWith(newP)
        // Optionally save to backend here
      } else {
        textarea.replaceWith(originalElement)
      }
    }

    textarea.addEventListener("blur", saveEdit)
    textarea.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        textarea.replaceWith(originalElement)
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        saveEdit()
      }
    })
  }

  deleteBlock(event) {
    if (!confirm("Are you sure you want to delete this question block?")) {
      return
    }

    const block = event.currentTarget.closest("[data-question-block]")
    if (!block) return

    // Animate removal
    block.style.transition = "opacity 0.2s ease-out"
    block.style.opacity = "0"

    setTimeout(() => {
      block.remove()
    }, 200)
  }

  // Edit greeting message
  editGreeting(event) {
    const section = event.currentTarget.closest(".flex.flex-col.gap-3")
    if (!section) return

    const markdownContainer = section.querySelector("[data-controller='markdown-render']")
    if (!markdownContainer) return

    // Prevent double-edit
    if (markdownContainer.querySelector("textarea")) return

    // Read raw markdown stored by markdown-render controller (preserves syntax like **bold**)
    let currentContent = markdownContainer.dataset.rawMarkdown || ""
    if (!currentContent) {
      const allParagraphs = markdownContainer.querySelectorAll("p")
      if (allParagraphs.length > 0) {
        currentContent = Array.from(allParagraphs).map(p => p.textContent).join("\n\n")
      } else {
        currentContent = markdownContainer.textContent.trim()
      }
    }

    // Create textarea for editing
    const textarea = document.createElement("textarea")
    textarea.value = currentContent
    textarea.className = "w-full px-3.5 py-2.5 bg-white border border-[#4427BF] rounded-lg text-sm text-[#030712] focus:outline-none focus:ring-2 focus:ring-[#4427BF] resize-none"
    textarea.rows = 6

    // Store original HTML for revert
    const originalHTML = markdownContainer.innerHTML

    // Replace markdown content with textarea
    markdownContainer.innerHTML = ""
    markdownContainer.appendChild(textarea)
    textarea.focus()
    textarea.select()

    let isEditing = true

    // Cleanup function
    const cleanup = () => {
      isEditing = false
      textarea.removeEventListener("blur", onBlur)
      textarea.removeEventListener("keydown", onKeydown)
    }

    const saveEdit = async () => {
      if (!isEditing) return
      cleanup()

      const newContent = textarea.value.trim()
      if (newContent !== currentContent) {
        // Re-render markdown
        markdownContainer.innerHTML = DOMPurify.sanitize(marked.parse(newContent))
        markdownContainer.dataset.rawMarkdown = newContent

        // Save to backend
        const saved = await this.saveWelcomeMessage(newContent)

        if (!saved) {
          // Revert if save failed
          markdownContainer.innerHTML = originalHTML
          markdownContainer.dataset.rawMarkdown = currentContent
        }
      } else {
        markdownContainer.innerHTML = originalHTML
      }
    }

    const onBlur = () => {
      if (isEditing) saveEdit()
    }

    const onKeydown = (e) => {
      if (!isEditing) return
      if (e.key === "Escape") {
        cleanup()
        markdownContainer.innerHTML = originalHTML
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        saveEdit()
      }
    }

    // Event listeners
    textarea.addEventListener("blur", onBlur)
    textarea.addEventListener("keydown", onKeydown)
  }

  // Save welcome message to guideline_data
  async saveWelcomeMessage(welcomeMessage) {
    // Get current guideline data from activity
    const guidelineData = this.extractGuidelineData()

    // Add welcome_message field
    guidelineData.welcome_message = welcomeMessage

    // Save to backend
    return await this.saveToBackend({
      guideline_data: guidelineData
    })
  }

  // Edit section content
  editSection(event) {
    const section = event.currentTarget.closest(".flex.flex-col.gap-3")
    if (!section) return

    const contentEl = section.querySelector("p, ul, ol")
    if (!contentEl) return

    const currentContent = contentEl.textContent.trim()
    const isList = contentEl.tagName === "UL" || contentEl.tagName === "OL"

    // Create textarea for editing
    const textarea = document.createElement("textarea")
    textarea.value = currentContent
    textarea.className = "w-full px-3.5 py-2.5 bg-white border border-[#4427BF] rounded-lg text-sm text-[#030712] focus:outline-none focus:ring-2 focus:ring-[#4427BF] resize-none"
    textarea.rows = isList ? 8 : 4

    // Store original element
    const originalElement = contentEl.cloneNode(true)

    // Replace content with textarea
    contentEl.replaceWith(textarea)
    textarea.focus()
    textarea.select()

    // Save function
    const saveEdit = async () => {
      const newContent = textarea.value.trim()
      if (newContent && newContent !== currentContent) {
        // Create new element with updated content
        let newElement
        if (isList) {
          newElement = document.createElement(contentEl.tagName)
          newElement.className = originalElement.className

          // Split by newlines and create list items
          const items = newContent.split("\n").filter(item => item.trim())
          items.forEach(item => {
            const li = document.createElement("li")
            li.textContent = item.replace(/^[-•*]\s*/, "").trim()
            newElement.appendChild(li)
          })
        } else {
          newElement = document.createElement("p")
          newElement.className = originalElement.className
          newElement.textContent = newContent
        }

        textarea.replaceWith(newElement)

        // Save to backend
        await this.saveGuidelineData()
      } else {
        textarea.replaceWith(originalElement)
      }
    }

    // Event listeners
    textarea.addEventListener("blur", saveEdit)
    textarea.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        textarea.replaceWith(originalElement)
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        saveEdit()
      }
    })
  }

  // Shuffle section (for randomizing questions)
  shuffleSection(event) {
    const section = event.currentTarget.closest(".flex.flex-col.gap-3")
    if (!section) return

    const list = section.querySelector("ol")
    if (!list) return

    // Get all list items
    const items = Array.from(list.children)

    // Shuffle array
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[items[i], items[j]] = [items[j], items[i]]
    }

    // Clear list and re-append in shuffled order
    list.innerHTML = ""
    items.forEach(item => list.appendChild(item))

    // Save to backend
    this.saveGuidelineData()
  }

  // Delete section
  deleteSection(event) {
    if (!confirm("Are you sure you want to delete this section?")) {
      return
    }

    const section = event.currentTarget.closest(".flex.flex-col.gap-3")
    if (!section) return

    // Get section info before removing
    const title = section.querySelector(".font-semibold")?.textContent.trim()
    const key = section.dataset.sectionKey || this.titleToKey(title)

    // Determine which tab/persona this section belongs to
    const tabContent = section.closest('[data-tabs-target="content"]')
    const personaValue = tabContent?.dataset.value

    console.log(`Deleting section: "${title}" (key: ${key}) from ${personaValue} tab`)

    // Animate removal
    section.style.transition = "opacity 0.2s ease-out"
    section.style.opacity = "0"

    setTimeout(() => {
      section.remove()

      // Delete from backend by sending the specific key to remove
      this.deleteSectionBackend(personaValue, key)
    }, 200)
  }

  updateSeedIntents(event) {
    const value = event.currentTarget.value
    clearTimeout(this.seedIntentsTimer)

    this.seedIntentsTimer = setTimeout(() => {
      this.saveToBackend({
        guideline_data: {
          seed_intents: value
        }
      })
    }, 500)
  }

  // Delete specific section from backend
  async deleteSectionBackend(personaValue, sectionKey) {
    if (!sectionKey) return

    // Build the delete instruction
    const deleteData = {
      delete_section: {
        persona: personaValue === "general" ? null : personaValue,
        key: sectionKey
      }
    }

    await this.saveToBackend(deleteData)
  }

  // Save to backend
  async saveToBackend(data) {
    if (!this.hasSaveUrlValue) {
      console.error("Save URL not configured")
      return false
    }

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        if (window.toastController) {
          window.toastController.success("Changes saved successfully")
        }
        return true
      } else {
        if (window.toastController) {
          window.toastController.error("Failed to save changes")
        }
        return false
      }
    } catch (error) {
      console.error("Save error:", error)
      if (window.toastController) {
        window.toastController.error("Failed to save changes")
      }
      return false
    }
  }

  // Save entire guideline data structure
  async saveGuidelineData() {
    // Extract current guideline structure from DOM
    const guidelineData = this.extractGuidelineData()

    // Save to backend
    await this.saveToBackend({
      guideline_data: guidelineData
    })
  }

  // Mapping from rendered section titles to the correct data keys.
  // The GuidelineGenerationService stores content under specific keys
  // (e.g. "key_themes", "traits") but the UI renders human-readable
  // titles like "Key Themes to Explore", "Typical Traits".
  // Naively converting those titles to snake_case produces wrong keys
  // ("key_themes_to_explore", "typical_traits") that diverge from the
  // actual data structure, causing duplicate entries and lost edits.
  static sectionTitleToKey = {
    "key themes to explore": "key_themes",
    "general approach": "general_approach",
    "typical traits": "traits",
    "focus areas": "focus_areas",
    "key questions": "key_questions",
    "probing techniques": "probing_techniques",
    "things to avoid": "things_to_avoid"
  }

  // Convert a rendered section title to the correct guideline_data key
  titleToKey(title) {
    const normalized = title.toLowerCase().trim()
    return this.constructor.sectionTitleToKey[normalized]
      || normalized.replace(/\s+/g, "_")
  }

  // Get the data key for a content section div.
  // Prefers the explicit data-section-key attribute (set in the ERB template)
  // so that the mapping is always correct regardless of the displayed title.
  sectionKey(div) {
    return div.dataset.sectionKey || this.titleToKey(div.querySelector(".font-semibold")?.textContent.trim() || "")
  }

  // Extract guideline data from current DOM state
  extractGuidelineData() {
    const data = {
      content: {
        personas: {}
      },
      personas: []
    }

    // Extract personas from sidebar
    const personaButtons = this.personaButtonTargets
    personaButtons.forEach(btn => {
      const personaName = btn.textContent.trim()
      if (personaName !== "General") {
        data.personas.push(personaName)
      }
    })

    // Extract content for ALL persona tabs (visible or not)
    const contentSections = this.element.querySelectorAll('[data-tabs-target="content"]')
    contentSections.forEach(section => {
      const personaValue = section.dataset.value

      // Find all content sections with white background (actual content sections)
      const contentDivs = section.querySelectorAll(".flex.flex-col.gap-3.p-5.bg-white.border.border-\\[\\#E4E5E8\\].rounded-lg")

      if (personaValue === "general") {
        // Extract general sections - only sections that exist in DOM
        contentDivs.forEach(div => {
          const title = div.querySelector(".font-semibold")?.textContent.trim()
          const content = div.querySelector("p, ul, ol")

          if (title && content) {
            const key = this.sectionKey(div)

            if (content.tagName === "UL" || content.tagName === "OL") {
              data.content[key] = Array.from(content.children).map(li => li.textContent.trim())
            } else {
              data.content[key] = content.textContent.trim()
            }
          }
        })
      } else {
        // Extract persona-specific sections
        const personaName = data.personas.find(p => p.toLowerCase().replace(/\s+/g, "-") === personaValue)
        if (!personaName) return

        const personaData = {}
        contentDivs.forEach(div => {
          const title = div.querySelector(".font-semibold")?.textContent.trim()
          const content = div.querySelector("p, ul, ol")

          if (title && content) {
            const key = this.sectionKey(div)

            if (content.tagName === "UL" || content.tagName === "OL") {
              personaData[key] = Array.from(content.children).map(li => li.textContent.trim())
            } else {
              personaData[key] = content.textContent.trim()
            }
          }
        })

        // Only add persona data if there are sections
        if (Object.keys(personaData).length > 0) {
          data.content.personas[personaName] = personaData
        }
      }
    })

    return data
  }
}
